import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../App';
import { LoadingSpinner } from './ui/loading-spinner';

const OnboardingRedirect = ({ children, currentUser, user, subscriptionStatus, appLoading, isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectAttempted, setRedirectAttempted] = useState(false);
    // Improved debug logging
  const logDebug = (message, data) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[OnboardingRedirect DEBUG] ${message}`, data || '');
    }
  };

  // Combine user properties from both user and currentUser props
  const userInfo = currentUser || user;

  useEffect(() => {
    // Log a separator to make it easier to trace through logs
    logDebug("-------- New OnboardingRedirect Check --------");
    
    // If user is an admin, bypass all onboarding checks
    if (isAdmin) {
      logDebug("Admin user, bypassing onboarding checks in OnboardingRedirect.");
      return;
    }

    logDebug("Current state:", {
      pathname: location.pathname,
      appLoading,
      userInfoExists: !!userInfo,
      userID: userInfo?.uid,
      userEmail: userInfo?.email,
      consentGiven: userInfo?.consentGiven,
      profileComplete: userInfo?.profileComplete,
      role: userInfo?.role,
      billingComplete: userInfo?.billingComplete,
      isOnboardingPaused: userInfo?.isOnboardingPaused,
      subscriptionStatus: subscriptionStatus?.status,
      redirectAttempted
    });

    // If app is still loading, wait
    if (appLoading) {
      logDebug("App is loading, pausing redirect logic.");
      return;
    }

    // Wait for verification that userInfo is available or definitively null
    if (userInfo === undefined) {
      logDebug("userInfo is undefined. Waiting for authentication check to complete.");
      return;
    }

    // If no user and app is done loading, ensure we're at HOME
    if (!userInfo) {
      logDebug("No userInfo. Current path:", location.pathname);
      if (location.pathname !== ROUTES.HOME) {
        logDebug("Redirecting to HOME because no userInfo.");
        navigate(ROUTES.HOME, { replace: true });
        setRedirectAttempted(true);
      } else {
        logDebug("Already on HOME, no userInfo. Splashscreen will handle.");
      }
      return;
    }

    logDebug("User exists with ID " + userInfo.uid + ". Proceeding with onboarding checks.");

    // Destructure user properties for checks
    const { consentGiven, profileComplete, role, billingComplete, isOnboardingPaused } = userInfo;

    // If onboarding is paused, skip checks
    if (isOnboardingPaused) {
      logDebug("Onboarding is PAUSED. No redirection will occur.");
      return;
    }
    logDebug("Onboarding is NOT paused.");

    // 1. Check for consent
    if (consentGiven !== 'true') {
      logDebug(`Consent check: consentGiven is '${consentGiven}'. Needs to be 'true'.`);
      if (location.pathname !== ROUTES.HOME) {
        logDebug("Redirecting to HOME for privacy dialog.");
        navigate(ROUTES.HOME, { replace: true, state: { showPrivacyPolicy: true } });
        setRedirectAttempted(true);
      } else {
        logDebug("Already on HOME. Splashscreen should show privacy dialog.");
      }
      return;
    }
    logDebug("Consent check PASSED (consentGiven is 'true').");

    // 2. Check for profile completion AND role selection
    const roleSelected = role && role.trim() !== '';
    const profileCompleteAndRoleSelected = profileComplete === true && roleSelected;
    
    if (!profileCompleteAndRoleSelected) {
      logDebug(`Profile/Role check: profileComplete is '${profileComplete}', role is '${role}'. Both must be complete.`);
      
      // REMOVED: Profile form logic - let ProtectedRoute handle redirection to /profile
      logDebug("Profile incomplete, letting ProtectedRoute handle redirection to /profile");
      return;
    }
    
    logDebug("Profile completion AND role selection checks PASSED.");

    // 3. Check for billing completion / active subscription
    let needsBilling = true; // Default assumption

    if (userInfo.role === 'Practice Manager') {
      // Practice Managers skip billing in MVP - no paid offering yet
      logDebug("User is Practice Manager. Skipping billing step for MVP.");
      needsBilling = false;
    } else {
      // For all other roles, check if billing is needed based on their flags or active subscription.
      const isBillingDone = userInfo.billingComplete === true || userInfo.selectedPlan;
      const isActiveSubscription = subscriptionStatus?.status === 'active' || subscriptionStatus?.subscription?.status === 'active';
      
      if (isBillingDone || isActiveSubscription) {
        needsBilling = false;
      }
      logDebug(`Non-PM Billing Check: billingComplete='${userInfo.billingComplete}', selectedPlan='${userInfo.selectedPlan}', subscriptionStatus='${subscriptionStatus?.status || subscriptionStatus?.subscription?.status}'. Needs billing: ${needsBilling}`);
    }
    
    if (needsBilling) {
      logDebug(`Billing check: billingComplete is '${userInfo.billingComplete}', selectedPlan is '${userInfo.selectedPlan}', subscriptionStatus is '${subscriptionStatus?.status}'. Needs billing.`);
      if (location.pathname !== ROUTES.BILLING) {
        logDebug("Redirecting to BILLING for plan selection/payment.");
        navigate(ROUTES.BILLING, { 
          replace: true, 
          state: { 
            onboarding: true, 
            forceShowPlans: true 
          } 
        });
        setRedirectAttempted(true);
      } else {
        logDebug("Already on BILLING page. BillingSettings component should handle plan display.");
      }
      return; // Stop further checks
    }
    logDebug("Billing check PASSED (billingComplete or selectedPlan is true, or subscription is active).");
    
    logDebug("All onboarding checks passed or on allowed page.");
    
    // ENHANCED: Role-aware dashboard redirection for all completion scenarios
    if (userInfo.role === 'Practice Manager') {
      // Practice Managers should always go to practice dashboard when onboarding is complete
      if (location.pathname === ROUTES.BILLING || location.pathname === ROUTES.PROFILE || location.pathname === ROUTES.HOME) {
        const targetDashboard = ROUTES.PRACTICE_DASHBOARD;
        logDebug(`Practice Manager onboarding complete, redirecting to ${targetDashboard}.`);
        navigate(targetDashboard, { replace: true });
        return;
      }
    } else {
      // Regular users - existing logic
      if (location.pathname === ROUTES.BILLING && !needsBilling) {
        const targetDashboard = ROUTES.DASHBOARD;
        logDebug(`Billing complete and on billing page, redirecting to ${targetDashboard}.`);
        navigate(targetDashboard, { replace: true });
        return;
      }
      
      if (location.pathname === ROUTES.PROFILE && profileCompleteAndRoleSelected && !needsBilling) {
        const targetDashboard = ROUTES.DASHBOARD;
        logDebug(`Profile complete and no billing needed, redirecting to ${targetDashboard}.`);
        navigate(targetDashboard, { replace: true });
        return;
      }
    }
    
  }, [userInfo, subscriptionStatus, appLoading, navigate, location.pathname, location.state, redirectAttempted, isAdmin]);

  // If user is an admin, render children directly, bypassing all other rendering logic.
  if (isAdmin) {
    logDebug("OnboardingRedirect: Admin user, rendering children directly.");
    return children;
  }

  // 1. Show loading spinner while app is loading
  if (appLoading) {
    logDebug("Rendering loading spinner (appLoading is true).");
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // 2. Show spinner if we don't have user data yet but we've attempted a redirect
  if (!userInfo && redirectAttempted) {
    logDebug("Rendering loading spinner after redirect attempt.");
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-400">Redirecting to login...</p>
      </div>
    );
  }
  
  // 3. Otherwise render children
  logDebug("Rendering children.");
  return children;
};

export default OnboardingRedirect;
